import { AuthProvider } from 'react-admin'
import { clearKnownAcks } from './alerts/alertAckEvents'
import { endUsageSession, startUsageSession } from './analytics/usageTracker'
import { deadlineFetch } from './hooks/customEndpointFetch'
import { clearUserScopedStoreKeys, GLUCOSE_UNIT_STORAGE_KEY } from './storeConfig'
import { resolveGlucoseUnitPreference } from './utils/glucoseDefaults'

const apiUrl = import.meta.env.VITE_BACKEND_API_URL

const COLLECTION_SLUG = 'users'

// Roles that can use the vet portal. Pet owners (role: 'user') belong on the
// Adapet Medical mobile app; without this gate they can sign in and then
// silently 403 on every dashboard list query (pets/devices/metrics/...)
// because the Payload access controls only grant clinic + internal roles
// read access to those collections.
export const VETPO_ALLOWED_ROLES: ReadonlySet<string> = new Set([
  'admin',
  'cms',
  'it',
  'vet',
  'vetadmin',
  'siteadmin',
  'account_manager',
])

const isAllowedRole = (role: unknown): boolean =>
  typeof role === 'string' && VETPO_ALLOWED_ROLES.has(role)

// Use sessionStorage instead of localStorage for better security
// sessionStorage is cleared when the browser tab closes, limiting token exposure window
const getToken = () => sessionStorage.getItem('token')
const setToken = (token: string) => sessionStorage.setItem('token', token)
const removeToken = () => {
  sessionStorage.removeItem('token')
  sessionStorage.removeItem('identity_cache')
}

// HttpOnly-cookie SSO fallback.
//
// The real session lives in the `payload-token` cookie, which Payload sets
// HttpOnly — JS can't read it to mirror into sessionStorage. sessionStorage is
// ALSO per-tab, so a portal opened in a new tab (e.g. the marketing-site nav's
// "Vet Portal" link opens in a new tab) or revisited after the tab closed has
// an empty sessionStorage even though the user holds a valid session cookie.
// Pre-fix the SPA bailed to its own login the instant sessionStorage was empty,
// never trying the cookie — the "logged in but can't enter Vet Portal" loop.
//
// Fix: when there's no sessionStorage token, still call GET /api/users/me with
// `credentials: 'include'`; the browser attaches the HttpOnly cookie and Payload
// authenticates the request. The dataProvider's same-origin `/api` calls also
// carry the cookie automatically, so the rest of the app works token-lessly.
//
// `cookieSessionActive` records that we authenticated via cookie (no token) so
// logout still POSTs /logout to clear the HttpOnly cookie — otherwise "Sign
// out" would leave the cookie valid and silently re-auth the user.
//
// This is now the FALLBACK case, not the steady state: `fetchIdentityFromServer`
// pins a cookie-only tab to the token `/me` echoes the first time it succeeds,
// so `cookieSessionActive` only stays true across requests when the server
// didn't echo one. A cookie the browser sends is read fresh on every request
// and is shared by every tab, so an unpinned tab silently runs as whichever
// account most recently signed in anywhere in the browser — the pin exists so
// a tab's identity is stable once established (TLA+ SessionAuth S2).
let cookieSessionActive = false
export const __resetCookieSessionForTests = () => {
  cookieSessionActive = false
}

const IDENTITY_CACHE_TTL = 5 * 60 * 1000 // 5 minutes

// Resolve identity from /api/users/me. Sends the Bearer token when present and
// ALWAYS includes credentials so an HttpOnly-cookie-only session (empty
// sessionStorage) still authenticates. Returns:
//   data       → authenticated, role allowed
//   null       → definitively no usable session (anonymous, 401, or role gate)
//   undefined  → couldn't determine (403/5xx) — keep any existing session
const fetchIdentityFromServer = async (): Promise<any | null | undefined> => {
  const token = getToken()
  const headers = new Headers({ Accept: 'application/json' })
  if (token) headers.set('Authorization', `Bearer ${token}`)

  // deadlineFetch (L-044): a stalled backend rejects within REQUEST_TIMEOUT_MS
  // instead of hanging <Admin>'s boot gate; checkAuth's catch already treats a
  // network/timeout rejection as "assume token still valid", and
  // getAlertAccess maps it to 'unknown' (the degraded-feed state).
  const response = await deadlineFetch(`/api/${COLLECTION_SLUG}/me`, {
    method: 'GET',
    headers,
    credentials: 'include', // attach the HttpOnly payload-token cookie
  })

  if (response.status === 401) {
    removeToken()
    return null
  }
  if (response.status === 403) {
    // /me returning 403 is unusual but should not nuke the session — the
    // token/cookie is still valid, the user just lacks read access to their own
    // record (misconfigured access rule). Treat as "couldn't load identity"
    // without logging out.
    return undefined
  }
  if (response.status !== 200) {
    return undefined // Server error - don't cache, don't logout
  }

  const data = await response.json()
  const user = data?.user ?? data
  // Payload answers 200 { user: null } for an unauthenticated request (no token
  // AND no valid cookie). Treat that, and any role the portal doesn't permit,
  // as "no session": drop the token so the user is bounced to login instead of
  // landing on a dashboard that 403s on every list query.
  if (!user || !isAllowedRole(user?.role)) {
    removeToken()
    return null
  }
  if (!token) {
    // Cookie-only boot. Pin this tab to the session /me just resolved by
    // adopting the token it echoes (Payload does this unless ada-core sets
    // removeTokenFromResponses, which it does not today). From here the tab
    // sends an Authorization header, which Payload's jwtOrder always prefers
    // over the shared cookie, so this tab keeps running as the account it
    // shows even after another tab signs in as someone else on that cookie
    // (TLA+ SessionAuth S2 — a cookie-only tab used to silently follow the
    // cookie to whichever account last signed in browser-wide). Without an
    // echoed token to adopt, fall back to the pre-fix cookie-trusting
    // behavior so logout still knows to clear it server-side.
    if (typeof data?.token === 'string' && data.token) {
      setToken(data.token)
      cookieSessionActive = false
    } else {
      cookieSessionActive = true
    }
  }
  return data
}

// Cache /api/users/me to avoid redundant calls on every route navigation
const getCachedIdentity = async (): Promise<any | null | undefined> => {
  const cached = sessionStorage.getItem('identity_cache')
  if (cached) {
    try {
      const { data, timestamp } = JSON.parse(cached)
      if (Date.now() - timestamp < IDENTITY_CACHE_TTL) {
        if (!isAllowedRole((data?.user || data)?.role)) {
          removeToken()
          return null
        }
        if (!getToken()) cookieSessionActive = true
        return data
      }
    } catch {
      // Invalid cache, will refetch
    }
    sessionStorage.removeItem('identity_cache')
  }

  const data = await fetchIdentityFromServer()
  if (data) {
    sessionStorage.setItem('identity_cache', JSON.stringify({ data, timestamp: Date.now() }))
  }
  return data
}

// Debounce for the 403 → token re-verification below: a dashboard load fires
// many list queries, and a dead token turns ALL of them into 403s at once.
// One /me round trip per interval is enough to classify the whole burst.
const TOKEN_RECHECK_INTERVAL_MS = 30 * 1000
let lastTokenRecheckAt = 0
export const __resetTokenRecheckForTests = () => {
  lastTokenRecheckAt = 0
}

/**
 * Alert-feature access, as a THREE-state answer.
 *
 * 'denied' and 'unknown' are not the same thing and must never be collapsed.
 * `getCachedIdentity()` yields `undefined` for any non-200 (fetchIdentityFromServer
 * returns undefined on a server error — deliberately, so a 500 does not log the
 * user out), and it throws outright on a network failure. The previous boolean
 * version folded BOTH into `false`, i.e. into "this role may not see alerts".
 *
 * That made a transient blip render as an authoritative statement about the
 * user's account: SimpleAlertPanel showed "alerts are not available for your
 * role" to a vet whose role was fine (eng-brain P32-3). The panel's copy came
 * from P24-2, which correctly stopped the panel claiming "no recent alerts" —
 * but by asserting something MORE specific it made the failure path MORE
 * confidently wrong. A fix that changes WHAT is asserted has to check WHY the
 * input can be false.
 */
export type AlertAccess = 'allowed' | 'denied' | 'unknown'

export const getAlertAccess = async (): Promise<AlertAccess> => {
  try {
    const data = await getCachedIdentity()
    // undefined = we could not read the identity (5xx / 403-on-own-record).
    // That is NOT a role decision, so it must not be reported as one.
    if (!data) return 'unknown'
    const user = data.user || data
    return user.role === 'admin' || user.role === 'vet' ? 'allowed' : 'denied'
  } catch {
    // Network failure — same reasoning: we do not know, so say so.
    return 'unknown'
  }
}

/**
 * Boolean form, kept for callers that only need "may I poll?".
 * NOTE: 'unknown' is deliberately falsy here — never start polling on a
 * guess — but a UI must use getAlertAccess() so it can tell the user which
 * of the two falsy cases it is in.
 */
export const canAccessAlerts = async (): Promise<boolean> => (await getAlertAccess()) === 'allowed'

// Shared post-authentication handling for BOTH password and OAuth login. Given
// a freshly issued { token, user }, enforce the vetpo role gate, persist the
// token, reset user-scoped store state, and seed the glucose-unit preference.
// Throws a translatable error key if the role isn't allowed in vetpo.
const applyAuthResult = (token: string, user: any): void => {
  if (!isAllowedRole(user?.role)) {
    // Auth succeeded server-side but this role can't use vetpo (typically
    // role 'user' — pet owners belong on the mobile app). Fire-and-forget
    // logout so the server-side session is invalidated, and never store token.
    if (token) {
      deadlineFetch(`/api/${COLLECTION_SLUG}/logout`, {
        method: 'POST',
        headers: new Headers({ Authorization: `Bearer ${token}` }),
      }).catch(() => {})
    }
    throw new Error('pos.auth.login.role_not_allowed')
  }
  setToken(token)
  // The tab may already hold ANOTHER account's identity_cache: a reload keeps
  // sessionStorage, and the invite / SSO pages run this on a reload of a tab
  // that was signed in. Left in place, the <Admin> boots showing the old
  // user's name, role and clinic for up to IDENTITY_CACHE_TTL while every
  // request runs as the new one (TLA+ SessionAuth S3).
  sessionStorage.removeItem('identity_cache')
  // Module-level per-session state survives a sign-out/sign-in without a
  // reload: drop what a previous session left (eng-brain P69-4, P69-5).
  startUsageSession()
  clearKnownAcks()
  // Drop the previous session's user-scoped store state FIRST (selectedPetId,
  // selectedDeviceId, per-pet ranges). glucoseUnit is intentionally NOT cleared
  // there — see clearUserScopedStoreKeys.
  clearUserScopedStoreKeys()
  // localStorage WINS: this browser's last chosen unit persists and takes
  // priority over the cloud profile. Only seed from the cloud profile (→
  // geographic → mg/dL) when this browser has no saved choice yet. An explicit
  // change still PATCHes the cloud (useGlucoseUnitPreference), so other devices
  // inherit it as THEIR fallback.
  if (localStorage.getItem(GLUCOSE_UNIT_STORAGE_KEY) == null) {
    const glucoseUnit = resolveGlucoseUnitPreference(user?.settings?.glucoseUnit, user)
    localStorage.setItem(GLUCOSE_UNIT_STORAGE_KEY, JSON.stringify(glucoseUnit))
  }
}

// Exchange a verified provider ID token for a vetpo session via the backend's
// social-login endpoint. context:'staff' so new accounts land as unprivileged
// vets in the existing clinic-verification lifecycle (parity with email vet
// self-signup) — never elevated on first login.
const oauthLogin = async (provider: 'google' | 'microsoft', idToken: string): Promise<void> => {
  const response = await deadlineFetch(`/api/${COLLECTION_SLUG}/oauth/login`, {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ provider, idToken, context: 'staff' }),
  })
  if (response.status < 200 || response.status >= 300) {
    const text = await response.text().catch(() => '')
    let backendMessage = ''
    try {
      const parsed = JSON.parse(text)
      backendMessage = parsed?.errors?.[0]?.message || parsed?.message || ''
    } catch {
      // non-JSON — leave empty
    }
    if (response.status === 401) {
      throw new Error('pos.auth.login.oauth_failed')
    }
    throw new Error(backendMessage || 'pos.auth.login.oauth_failed')
  }
  const { token, user } = await response.json()
  applyAuthResult(token, user)
}

// Apply an externally-obtained { token, user } as the vetpo session — used by
// pre-Admin standalone pages (clinic-team invite acceptance) that must
// authenticate BEFORE the Admin mounts. Runs the same role gate + store reset
// as password/OAuth login.
export const applyExternalAuthResult = (token: string, user: unknown): void => {
  applyAuthResult(token, user)
}

// Exchange an OPS deep-link SSO handoff token (short-lived, arrived via the
// #/sso URL fragment) for a normal session. The handoff is single-use — the
// backend deletes its session row on exchange — so a stale/replayed URL fails
// here and the landing page routes to /login instead. Shares applyAuthResult
// with the password + OAuth paths (role gate, token persist, store reset,
// glucose-unit seeding).
export const exchangeSsoHandoff = async (handoffToken: string): Promise<void> => {
  const response = await deadlineFetch(`/api/${COLLECTION_SLUG}/ops-sso/exchange`, {
    method: 'POST',
    headers: new Headers({ Authorization: `JWT ${handoffToken}` }),
  })
  if (response.status < 200 || response.status >= 300) {
    throw new Error('pos.auth.login.sso_failed')
  }
  const { token, user } = await response.json()
  applyAuthResult(token, user)
}

interface OAuthLoginParams {
  provider: 'google' | 'microsoft'
  idToken: string
}

export default {
  login: (params: { email: string; password: string } | OAuthLoginParams) => {
    // OAuth path: the Login page passes { provider, idToken } after the user
    // completes a Google / Microsoft sign-in client-side.
    if ('provider' in params && 'idToken' in params) {
      return oauthLogin(params.provider, params.idToken).catch((err) => {
        console.error(err)
        throw err
      })
    }
    const { email, password } = params
    // Normalize email client-side (trim + lowercase) so case-mixed input
    // matches the canonical form Payload stores. The server normalizes too,
    // but doing it here means identity caches and error messages echo the
    // normalized address back consistently.
    const normalizedEmail = (email || '').trim().toLowerCase()
    return deadlineFetch(`/api/${COLLECTION_SLUG}/login`, {
      method: 'POST',
      body: JSON.stringify({ email: normalizedEmail, password }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    })
      .then((response) => {
        if (response.status < 200 || response.status >= 300) {
          return response.text().then((text) => {
            // Try to extract a clean message from Payload's error envelope:
            //   { "errors": [{ "message": "..." }] }
            let backendMessage = ''
            try {
              const parsed = JSON.parse(text)
              backendMessage = parsed?.errors?.[0]?.message || parsed?.message || ''
            } catch {
              // Non-JSON response — leave backendMessage empty
            }

            // Map known backend messages to i18n keys so the notification
            // is localized instead of leaking raw English JSON.
            const lower = backendMessage.toLowerCase()
            if (lower.includes('verify your email')) {
              throw new Error('pos.auth.login.email_not_verified')
            }
            if (
              response.status === 401 ||
              lower.includes('invalid') ||
              lower.includes('incorrect') ||
              lower.includes('credentials')
            ) {
              throw new Error('pos.auth.login.invalid_credentials')
            }
            if (response.status === 423 || lower.includes('locked')) {
              throw new Error('pos.auth.login.account_locked')
            }

            // Fallback: surface the cleaned backend message if present,
            // otherwise a generic translatable key.
            throw new Error(backendMessage || 'pos.auth.login.generic_error')
          })
        }
        return response.json()
      })
      .then(({ token, user }) => {
        // Shared with the OAuth path: role gate + token persist + store reset +
        // glucose-unit seeding. See applyAuthResult for the (load-bearing)
        // rationale on store clearing and the RaStorevetpo.glucoseUnit key.
        applyAuthResult(token, user)
      })
      .catch((err) => {
        console.error(err)
        throw err
      })
  },
  logout: () => {
    // BEFORE the token is removed: send this session's queued usage events
    // under its own credentials, then clear the tab's per-session module state
    // (eng-brain P69-4, P69-5).
    endUsageSession()
    clearKnownAcks()
    const token = getToken()
    // Nothing to invalidate server-side — skip the network call. React-admin
    // calls logout() on a failed checkAuth (e.g. fresh anonymous page load), and
    // POST /api/users/logout with "Authorization: Bearer null" lands as a 400 in
    // the console. Harmless but noisy; short-circuit. We still POST when the
    // session is cookie-only (no token but cookieSessionActive) so the HttpOnly
    // payload-token cookie is actually cleared — otherwise "Sign out" leaves the
    // cookie valid and the SPA silently re-auths on the next load.
    if (!token && !cookieSessionActive) {
      clearUserScopedStoreKeys()
      return Promise.resolve()
    }
    const headers = new Headers()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    // Use .catch so a transient failure still clears local state — the user
    // pressed Sign Out and must not stay "logged in" in the UI. deadlineFetch
    // bounds the stall case too: Sign Out becomes locally effective within
    // REQUEST_TIMEOUT_MS even if the server never answers.
    return deadlineFetch(`/api/${COLLECTION_SLUG}/logout`, {
      method: 'POST',
      headers,
      credentials: 'include', // send the HttpOnly cookie so the server clears it
    })
      .catch(() => {})
      .then(() => {
        removeToken()
        cookieSessionActive = false
        // Clear user-scoped store state (glucose unit, selected pet/device,
        // per-pet target ranges) so the next user on this browser starts
        // clean instead of inheriting this session's selections — stale
        // selections trigger an unauthorized dashboard fan-out on the next
        // account (see storeConfig.ts). Login clears too, for sessions that
        // end without an explicit logout.
        clearUserScopedStoreKeys()
      })
  },
  checkAuth: () => {
    // No early sessionStorage-token gate: getCachedIdentity also resolves
    // HttpOnly-cookie-only sessions (returns null only when truly anonymous).
    return getCachedIdentity()
      .then((data) => {
        if (data === null) {
          throw new Error('Authentication check failed')
        }
        // Skip processing if we got undefined (server error case)
        if (data === undefined) return

        const user = data.user || data
        // Same rule as login (applyAuthResult): localStorage wins, so only
        // backfill from the cloud profile when the key is missing entirely
        // (e.g. first time on this browser, or the user cleared site data).
        // Never stomp a unit already present — it's either this browser's
        // saved choice or one the vet just changed this session.
        if (localStorage.getItem(GLUCOSE_UNIT_STORAGE_KEY) == null) {
          const glucoseUnit = resolveGlucoseUnitPreference(user?.settings?.glucoseUnit, user)
          localStorage.setItem(GLUCOSE_UNIT_STORAGE_KEY, JSON.stringify(glucoseUnit))
        }
      })
      .catch((error) => {
        if (error.message === 'Authentication check failed') {
          throw error
        }
        // For network errors - RESOLVE instead of reject to keep user logged in
        console.warn('checkAuth: Network/timeout error, assuming token still valid:', error.message)
        return
      })
  },
  checkError: async (error: { status: any }) => {
    const status = error.status
    // ⚠️ THERE ARE TWO 401/403 POLICIES IN THIS APP AND THEY ARE NOT THE SAME.
    //   • THIS one (VP-AUTH R21-R26) is the SESSION policy: it decides whether
    //     the user stays logged in.
    //   • `httpClient` in src/dataProvider/index.ts (VP-DATA R09-R15) is the
    //     TRANSPORT policy: it decides whether a single request is retried.
    // Changing one does NOT change the other, and they disagree on purpose —
    // the transport layer retries a first 401 once before giving up, while this
    // layer has no request to retry. Read both before "unifying" them.
    //
    // 401 = not authenticated → log out so the user can re-login.
    // 403 = authenticated but forbidden from THIS resource → do NOT log out.
    //   The token is still valid; just one record/endpoint is out of scope.
    //   Lumping 403 in here used to cause a hard logout when /api/metrics
    //   returned 403 on a single forbidden row inside a large list query
    //   (e.g. ?limit=10000), bouncing the vet to the login screen mid-load
    //   on every device click.
    if (status === 401) {
      removeToken()
      return Promise.reject(new Error('Unauthorized'))
    }
    if (status === 403) {
      // HOWEVER: Payload also answers 403 when the token itself no longer
      // resolves a user (expired session, deleted user, rotated JWT secret) —
      // the request is treated as anonymous and access control denies it.
      // With the 5-minute identity cache still warm, nothing re-checked the
      // token, so the dashboard kept re-firing large list queries with a dead
      // token (prod retry storm, 2026-06-05). On 403, re-verify the token
      // against /me at most once per TOKEN_RECHECK_INTERVAL_MS:
      //   identity gone   → the whole session is dead → real logout
      //   identity intact → genuine row-level 403     → keep the session
      //
      // A cookie-only session (no token, cookieSessionActive) re-checks too.
      // This gate predated cookie sessions and read `getToken() &&` alone, so
      // a tab opened on the cookie whose session then died — expired, or
      // signed out in ANOTHER tab, which kills the session and expires the
      // shared cookie — kept its dead session forever (TLA+ SessionAuth S1).
      // A tab with neither has no session to re-verify.
      const now = Date.now()
      if (
        (getToken() || cookieSessionActive) &&
        now - lastTokenRecheckAt >= TOKEN_RECHECK_INTERVAL_MS
      ) {
        lastTokenRecheckAt = now
        sessionStorage.removeItem('identity_cache')
        try {
          const data = await getCachedIdentity()
          if (data === null) {
            // null = /me resolved no usable user (getCachedIdentity already
            // dropped the token). undefined = /me errored (5xx/timeout) —
            // can't confirm the token is dead, so fall through and keep it.
            removeToken()
            return Promise.reject(new Error('Session expired'))
          }
        } catch {
          // Network failure — can't confirm the token is dead; keep session.
        }
      }
      return Promise.resolve()
    }
    // For all other errors, resolve to let the app handle them
    // (typically by surfacing a "Forbidden" or generic error notification).
    return Promise.resolve()
  },
  getPermissions: () => {
    // No token gate — cookie-only sessions resolve via getCachedIdentity too.
    return getCachedIdentity()
      .then((data) => {
        if (!data) return {}
        const user = data.user || data
        return { role: user.role }
      })
      .catch(() => ({}))
  },
  getIdentity: () => {
    // No token gate — cookie-only sessions resolve via getCachedIdentity too.
    return getCachedIdentity()
      .then((data) => {
        if (!data) {
          throw new Error('Failed to get user identity')
        }
        const user = data.user || data
        return {
          id: user.id,
          fullName: user.name || user.email,
          email: user.email,
          role: user.role,
          // Clinic display name — clinic_name legitimately lives on vet users
          // (see src/pets/getVetClinicName.ts ownership rule)
          clinicName: user.clinic_name || null,
          // Clinic Team Master/Sub: lets components tell a master (no parent)
          // from a sub-account. masterAccount may be an id or a populated obj.
          masterAccount:
            user.masterAccount && typeof user.masterAccount === 'object'
              ? user.masterAccount.id
              : (user.masterAccount ?? null),
          subAccountStatus: user.subAccountStatus ?? null,
        }
      })
      .catch((error) => {
        console.error('getIdentity error:', error)
        throw error
      })
  },
} as AuthProvider
